import uniqBy from "lodash/uniqBy";

import {filterFeaturesByValues} from "../FeatureCreator/FeaturesFilters/filtersFunctions";
import {AccessRights, PARTS} from "../../schemas/accessRightsModel";
import {FEATURE_ENABLED_PROPERTIES} from "./FeaturesSetupFeature/featureInterface";

/**
 * returns the same list for several features-setup components
 * @param store {object}
 * @param selectedOnly {boolean}
 * @param withFilters {boolean}
 * @return {object}
 */
export const getFilteredFeatures = (store, selectedOnly = false, withFilters = true) => {

  let {activeMode} = store.featuresCreatorNew;
  let featuresList = [...store.featuresSetupState.featuresList];
  const {selectedFilters} = store.featuresFiltersState;

  if (activeMode !== "All") {
    switch (activeMode) {
      case "Bathroom":
        featuresList = featuresList.filter(feat => feat.hasBathroom);
        break;
      case "Kitchen":
        featuresList = featuresList.filter(feat => feat.hasKitchen);
        break;
      default:
        break;
    }
  }

  if (selectedOnly) {
    featuresList = featuresList.filter(feature => feature.selected);
  }

  if (selectedFilters.length && withFilters) {
    featuresList = filterFeaturesByValues(featuresList, selectedFilters);
  }

  return featuresList
}

/**
 * returns the same list for several features-setup components
 * @param store {object}
 * @return {boolean}
 */
export const getFeatureEditPermission = (store) => {
  const AR = AccessRights.get(store);
  const {groupName} = store.featuresSetupState;

  let enablePermission = {visible: true, enabled: true};

  switch((groupName || "").toLowerCase()) {
    case "countertop":
      enablePermission = AR.checkRights(PARTS.FAB_EDIT_COUNTERTOP);
      break;
    case "center island countertop":
      enablePermission = AR.checkRights(PARTS.FAB_EDIT_CENTER_ISLAND_COUNTERTOP);
      break;
    case "backsplash":
      enablePermission = AR.checkRights(PARTS.FAB_EDIT_BACKSPLASH);
      break;
  }
  return {
    canEdit: AR.isSiteAdmin(false) || AR.v(PARTS.FAB_CREATE_EDIT_FEATURE),
    enablePermission
  }
}

/**
 * returns fixed value for number inputs
 * @param costValue {value}
 * @return {string}
 */
export const fixFeatureNumberValue = (costValue) => {
  if (!costValue) {
    return "";
  }
  if (typeof costValue !== "string") {
    if (typeof costValue === "number" && Math.round(costValue) !== costValue) {
      try {
        costValue = costValue.toFixed(2);
      } catch(ex) {
        console.log("Helpers toFixedPrice failed for ", costValue, ex);
      }
    }
    return costValue + "";
  }
  return costValue
}

/**
 * returns fixed value for number inputs
 * @param feature {featureInterface}
 * @param property {string}
 * @return {string}
 */
export const fixFeatureNumberValueForInput = (feature, property) => {
  let costValue = feature.priceLocked
    ? feature[property]
    : feature.unlinkedData && feature.unlinkedData.hasOwnProperty(property) ? feature.unlinkedData[property] : feature[property];
  return fixFeatureNumberValue(costValue)
}

const shippingProps = ["shippingName", "shippingCost", "shippingTime"];

/**
 * returns fixed value for number inputs
 * @param features {[featureInterface]}
 * @param sortByProperty {string}
 * @return {array}
 */
export const getShippingSetupList = (features, sortByProperty = "shippingCost") => {
  let deliveries = [];
  features.forEach((feature) => {
    const existingDeliveryIndex = deliveries.findIndex(delivery => {
      return sortByProperty === "shippingCost"
        ? shippingProps.every(property => delivery[property] === feature[property])
        : delivery[sortByProperty] === feature[sortByProperty]
    });
    if (existingDeliveryIndex > -1) {
      deliveries[existingDeliveryIndex].features.push(feature)
    } else {
      deliveries.push({
        shippingName: feature.shippingName,
        shippingCost: feature.shippingCost,
        shippingTime: feature.shippingTime,
        price: feature.price,
        features: [feature],
        useProperty: sortByProperty,
      })
    }
  })
  return deliveries
}

/**
 * returns bool
 * @param feature {featureInterface}
 * @param auth {object}
 * @return {boolean}
 */
export const isFeatureEnabled = (feature, auth) => {

  const {hasKitchenSub, hasBathroomSub, hasFireplaceSub, hasLaundryRoomSub, hasLeadFormSub} = auth;

  const toolSettings = getCurrentFeatureToolSettings(feature);
  if (!Array.isArray(toolSettings) || !toolSettings.length) {
    return false // just in case
  }
  if (hasFireplaceSub && !toolSettings.some(toolSettingsObject => toolSettingsObject.tool === "fireplace")) {
    toolSettings.push({
      tool: "fireplace",
      enableQuotingTool: false,
      enableVisualizer: false,
    })
  }
  // the feature is enabled if it has at least one checkbox turned on, and the subscription for this tool is also on
  return toolSettings.some(toolSettingsObject => {
    if (!toolSettingsObject.enableQuotingTool && !toolSettingsObject.enableVisualizer) {
      return false
    }
    switch (toolSettingsObject.tool) { // if fabricator doesn't have a subscription - return false (ignore enabled properties)
      case "bathroom":
        return hasBathroomSub
      case "fireplace":
        return hasFireplaceSub
      case "kitchen":
        return hasKitchenSub
      case "laundryroom":
      case "laundryRoom": // just in case
        return hasLaundryRoomSub
      case "leadform":
        return hasLeadFormSub
      default:
        return false
    }
  })
}

/**
 * returns sorted array
 * @param features {[featureInterface]}
 * @param auth {object}
 * @return {[featureInterface]}
 */
export const sortFeaturesArray = (features, auth) => {

  const allFeatures = features.map((feature) => {
    feature.isEnabled = isFeatureEnabled(feature, auth);
    if (!Array.isArray(feature.toolSettings)) { // just in case
      feature.toolSettings = [];
    }
    return feature
  });

  const enabledFeatures = allFeatures.filter(dbFeature => dbFeature.isEnabled);
  const disabledFeatures = allFeatures.filter(dbFeature => !dbFeature.isEnabled);

  const featuresList = [
    ...enabledFeatures.sort((a, b) => (a.name > b.name) ? 1 : -1),
    ...disabledFeatures.sort((a, b) => (a.name > b.name) ? 1 : -1)
  ]

  return featuresList
}

export const updateUnlinkedDataFromToolSettings = (unlinkedData, toolSettings) => {
  if (!Array.isArray(toolSettings) || !toolSettings.length) {
    return unlinkedData
  }
  const bvSetting = toolSettings.find(setting => setting.tool === "bathroom");
  const kvSetting = toolSettings.find(setting => setting.tool === "kitchen");
  if (bvSetting) {
    unlinkedData.hideFromBV = !bvSetting.enableVisualizer;
    unlinkedData.hideFromBVQT = !bvSetting.enableQuotingTool;
  }
  if (kvSetting) {
    unlinkedData.hideFromKV = !kvSetting.enableVisualizer;
    unlinkedData.hideFromKVQT = !kvSetting.enableQuotingTool;
  }
  return unlinkedData
}

export const getToolSettingsFromUnlinkedData = (feature) => {
  const {unlinkedData} = feature;
  let toolSettings = [], bvSetting, kvSetting;
  if (Array.isArray(feature.toolSettings)) { // here we take all original properties, like {created, createdBy_id} and so on
    const featureBvSetting = feature.toolSettings.find(setting => setting.tool === "bathroom");
    const featureKvSetting = feature.toolSettings.find(setting => setting.tool === "kitchen");
    if (featureBvSetting) {
      bvSetting = Object.assign({}, featureBvSetting);
    }
    if (featureKvSetting) {
      kvSetting = Object.assign({}, featureKvSetting);
    }
  }
  if (!bvSetting) {
    bvSetting = {tool: "bathroom"};
    toolSettings.push(bvSetting);
  }

  bvSetting.enableQuotingTool = !unlinkedData.hideFromBVQT;
  bvSetting.enableVisualizer = !unlinkedData.hideFromBV;

  if (!kvSetting) {
    kvSetting = {tool: "kitchen"};
    toolSettings.push(kvSetting);
  }
  kvSetting.enableQuotingTool = !unlinkedData.hideFromKVQT;
  kvSetting.enableVisualizer = !unlinkedData.hideFromKV;

  return toolSettings
}

export const getCurrentFeatureToolSettings = (feature) => {
  if (feature.unlinkedData) {
    if (!Array.isArray(feature.unlinkedData.toolSettings) || !feature.unlinkedData.toolSettings.length) {
      return getToolSettingsFromUnlinkedData(feature);
    }
    return feature.unlinkedData.toolSettings || []
  }
  return feature.toolSettings || []
}

export const getNewUnlinkedDataObject = (fromFeature, catId) => {
  let newData = {
    id: 0,
    fabricatorFeatureId: 0,
    featureGroupCategory_id: catId,
    price: fromFeature.price,
    cost: fromFeature.cost,
    complexLogic: null,
    displayName: null,
    hideFromKVQT: null, // not used anymore
    hideFromKV: null, // not used anymore
    hideFromBVQT: null, // not used anymore
    hideFromBV: null, // not used anymore
    shippingCost: 0,
    shippingTime: 0,
    shippingName: 0,
    toolSettings: fromFeature.toolSettings ? fromFeature.toolSettings.map(setting => Object.assign({}, setting)) : [],
  };
  if (newData.toolSettings) { // I think we can remove it, but let it be, for compatability
    newData = updateUnlinkedDataFromToolSettings(newData, newData.toolSettings);
  }
  return newData
}

export const getFeaturesStatuses = (allFeatures) => {
  let allHaveBv = true,
    allHaveBvQt = true,
    allHaveKv = true,
    allHaveKvQt = true,
    allUnlocked = true;

  for (let i = 0; i < allFeatures.length; i++) {
    const feature = allFeatures[i];
    const toolSettings = getCurrentFeatureToolSettings(feature);
    const bvSetting = toolSettings.find(setting => setting.tool === "bathroom");
    /*** Bathroom ***/
    if (!bvSetting || !bvSetting.enableVisualizer) {
      allHaveBv = false;
    }
    if (!bvSetting || !bvSetting.enableQuotingTool) {
      allHaveBvQt = false;
    }
    /*** Kitchen ***/
    const kvSetting = toolSettings.find(setting => setting.tool === "kitchen");
    if (!kvSetting || !kvSetting.enableVisualizer) {
      allHaveKv = false;
    }
    if (!kvSetting || !kvSetting.enableQuotingTool) {
      allHaveKvQt = false;
    }
    /*** Other ***/
    if (!feature.unlocked) {
      allUnlocked = false
    }
  }
  return {allHaveKv, allHaveKvQt, allHaveBv, allHaveBvQt, allUnlocked}
}

export const getEnabledFeaturesFromResponse = (responseData, auth) => {
  if (!responseData) {
    return []
  }
  let uniqueList = uniqBy([...(responseData.enabledFeatures || []),...(responseData.disabledFeatures || [])], "featureDefinitionId");
  return uniqueList.filter(feature => isFeatureEnabled(feature, auth));
}
